import { useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import { api } from "../lib/api";

const fmtRON = (v) => `${new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2 }).format(v)} RON`;

export default function ManualInvoicePrintPage() {
  const { id } = useParams();
  const [inv, setInv] = useState(null);
  const [error, setError] = useState(false);
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("kk_token")) { setAuthed(false); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (!authed) return;
    api.get("/admin/invoices").then((r) => {
      const found = r.data.find((x) => x.id === id);
      if (found) { setInv(found); setTimeout(() => window.print(), 700); }
      else setError(true);
    }).catch(() => setError(true));
  }, [authed, id]);

  if (authed === false) return <Navigate to="/admin/login" replace />;
  if (authed === null) return null;
  if (error) return <div className="p-10 text-center">Factura nu a fost gasita.</div>;
  if (!inv) return <div className="p-10 text-center text-neutral-500">Se incarca...</div>;

  return (
    <div data-testid="manual-invoice-print-page" className="tx-print bg-white text-black min-h-screen">
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print { .no-print { display: none !important; } body { background: white !important; } }
        .tx-print { font-family: 'Manrope', sans-serif; }
        .tx-print .display { font-family: 'Unbounded', sans-serif; letter-spacing: -0.02em; }
      `}</style>

      <div className="no-print fixed top-4 right-4 flex gap-2 z-50">
        <button data-testid="print-btn" onClick={() => window.print()} className="bg-[#00E5FF] text-black font-semibold px-4 py-2 text-sm uppercase tracking-wider">Printeaza</button>
        <button data-testid="close-btn" onClick={() => window.close()} className="border border-neutral-400 text-neutral-700 px-4 py-2 text-sm uppercase tracking-wider">Inchide</button>
      </div>

      <div className="max-w-3xl mx-auto p-8">
        <div className="flex items-start justify-between pb-6 border-b-4 border-black">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 bg-[#00E5FF] flex items-center justify-center display font-bold text-xl">TX</div>
            <div>
              <div className="display text-2xl font-bold uppercase leading-none">TROTI<span style={{ color: "#00B8CC" }}>-X</span></div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Magazin & Service trotinete electrice</div>
            </div>
          </div>
          <div className="text-right">
            <div className="display text-3xl font-bold uppercase">Factura</div>
            <div className="font-mono font-bold text-lg mt-1">{inv.invoice_number}</div>
            <div className="text-xs text-neutral-500 mt-1">Data: {new Date(inv.created_at).toLocaleDateString("ro-RO")}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mt-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: "#00B8CC" }}>// Furnizor</div>
            <div className="text-sm leading-relaxed">
              <div className="font-bold">A.C.M BITY POWER S.R.L. (TROTI-X)</div>
              CUI: 51193046 · Reg. Com.: J2025005538004<br />
              Sediu: Str. Constantin Brăiloiu, Nr. 5, Parter, Ap. 1, Timișoara<br />
              Punct de lucru: Bd. Liviu Rebreanu 104A, Timișoara<br />
              IBAN: RO26BTRLRONCRT0CW7080401 — Banca Transilvania<br />
              Tel: 0743 110 277
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold mb-2" style={{ color: "#00B8CC" }}>// Client</div>
            <div className="text-sm leading-relaxed">
              <div className="font-bold">{inv.customer_name}</div>
              {inv.customer_address && <>{inv.customer_address}<br /></>}
              {inv.customer_phone && <>Tel: {inv.customer_phone}<br /></>}
              {inv.customer_cui && <>CUI/CNP: {inv.customer_cui}</>}
            </div>
          </div>
        </div>

        <table className="w-full mt-8 text-sm border-collapse">
          <thead>
            <tr className="bg-black text-white">
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">#</th>
              <th className="text-left px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">Produs / Serviciu</th>
              <th className="text-center px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">Cant.</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">Pret unitar</th>
              <th className="text-right px-3 py-2.5 text-[10px] uppercase tracking-widest font-bold">Total</th>
            </tr>
          </thead>
          <tbody>
            {inv.items.map((it, i) => (
              <tr key={i} className="border-b border-neutral-300">
                <td className="px-3 py-3 text-neutral-500">{i + 1}</td>
                <td className="px-3 py-3 font-medium">{it.name}</td>
                <td className="px-3 py-3 text-center">{it.qty}</td>
                <td className="px-3 py-3 text-right">{fmtRON(it.price)}</td>
                <td className="px-3 py-3 text-right font-bold">{fmtRON(it.price * it.qty)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end mt-4">
          <div className="w-72 space-y-2 text-sm">
            <div className="flex justify-between items-center border-t-2 border-black pt-3">
              <span className="text-xs uppercase tracking-widest font-bold">Total de plata</span>
              <span className="display font-bold text-2xl">{fmtRON(inv.total)}</span>
            </div>
            <div className="flex justify-between text-neutral-600">
              <span>Metoda de plata</span>
              <span className="font-bold text-black">{inv.payment_method === "cash" ? "Numerar" : "Card"}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-12 mt-12">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-12">Semnatura furnizor</div>
            <div className="border-t border-black pt-2 text-[10px] text-neutral-500">A.C.M BITY POWER S.R.L.</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-600 mb-12">Semnatura client (primire)</div>
            <div className="border-t border-black pt-2 text-[10px] text-neutral-500">{inv.customer_name}</div>
          </div>
        </div>

        <div className="mt-10 pt-5 border-t border-neutral-300 text-center text-[10px] uppercase tracking-widest text-neutral-500">
          Multumim! · TROTI-X · 0743 110 277 · www.troti-x.com · {inv.invoice_number}
        </div>
      </div>
    </div>
  );
}
